import type { ReactNode } from 'react';
import styled from '@emotion/styled';
import { EditActionButtons } from './EditActionButtons';

type Props = {
  editButtonColor?: string;
  editingContent: ReactNode;
  isEditing: boolean;
  label: ReactNode;
  onCancel: () => void;
  onEdit: () => void;
  onSave: () => void;
  readOnlyContent: ReactNode;
  saveDisabled?: boolean;
};

/**
 * A labeled field with a read-only/editing content switch and the same circular edit/save/cancel
 * buttons (`EditActionButtons`) the Tag Details screen uses — SegmentProfile's field components
 * (Subtitle/Attributes/SampleData/Description) are its consumers. Distinct from
 * `Management/EditableFieldSection`: that one is still used by PatientJourneyManage, which
 * relies on its separately-disableable Cancel button (`cancelDisabled`), a behavior
 * `EditActionButtons`'/`FormButtons`' single `disabled` flag doesn't support — so rather than
 * changing that shared component's behavior for an unrelated screen, this is a parallel, leaner
 * version for consumers that don't need it.
 */
export const EditableFieldSection = ({
  editButtonColor,
  editingContent,
  isEditing,
  label,
  onCancel,
  onEdit,
  onSave,
  readOnlyContent,
  saveDisabled = false,
}: Props) => (
  <Field>
    <FieldHeader>
      <Label>{label}</Label>
      <EditActionButtons
        isEditing={isEditing}
        isSaving={saveDisabled}
        onEdit={onEdit}
        onSave={onSave}
        onCancel={onCancel}
        themedColor={editButtonColor}
      />
    </FieldHeader>
    {isEditing ? editingContent : readOnlyContent}
  </Field>
);

const Field = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
});

const FieldHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
});

const Label = styled('label')(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 12,
  color: theme.palette.gray.dark1,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
}));
